// src/components/Appointments/AppointmentFieldUpdateModal.jsx
// ✅ FIXED VERSION - Aligned with backend constants

import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  FiX,
  FiClock,
  FiTag,
  FiAlertCircle,
  FiTarget,
  FiFlag,
  FiBell,
  FiFileText,
} from "react-icons/fi";
import Button from "../../../components/common/Button";
import {
  updateAppointmentTypeThunk,
  updateAppointmentDurationThunk,
  updateAppointmentIntentThunk,
  updateAppointmentPriorityThunk,
  addAppointmentTagsThunk,
  removeAppointmentTagsThunk,
  updateAppointmentMetadataThunk,
  updateReminderPreferencesThunk,
} from "../../../store/thunks/appointmentThunks";
import toast from "react-hot-toast";

// ✅ FIXED: Match backend APPOINTMENT_TYPES exactly
const APPOINTMENT_TYPES = [
  { value: "PRODUCT_CONSULTATION", label: "Product Consultation" },
  { value: "CUSTOM_DESIGN", label: "Custom Design" },
  { value: "REPAIR_CONSULTATION", label: "Repair Consultation" },
  { value: "APPRAISAL", label: "Appraisal" },
  { value: "ENGAGEMENT_RING", label: "Engagement Ring" },
  { value: "WEDDING_JEWELRY", label: "Wedding Jewelry" },
  { value: "GENERAL_INQUIRY", label: "General Inquiry" },
  { value: "VIP_CLIENT", label: "VIP Client" },
  { value: "COLLECTION_VIEWING", label: "Collection Viewing" },
];

// ✅ FIXED: Match backend PRIORITY_LEVELS exactly
const PRIORITIES = [
  { value: "LOW", label: "Low", color: "bg-gray-200 text-gray-700" },
  { value: "MEDIUM", label: "Medium", color: "bg-blue-200 text-blue-700" }, // ✅ MEDIUM not NORMAL
  { value: "HIGH", label: "High", color: "bg-yellow-200 text-yellow-700" },
  { value: "URGENT", label: "Urgent", color: "bg-red-200 text-red-700" },
];

// ✅ FIXED: Match backend PURPOSE_TYPES
const PURPOSE_TYPES = [
  { value: "BROWSING", label: "Browsing" },
  { value: "BUYING", label: "Buying" },
  { value: "CUSTOM_DESIGN", label: "Custom Design" },
  { value: "EXCHANGE_VALUATION", label: "Exchange Valuation" },
  { value: "REPAIR_INQUIRY", label: "Repair Inquiry" },
  { value: "GIFT_SHOPPING", label: "Gift Shopping" },
  { value: "WEDDING_PLANNING", label: "Wedding Planning" },
  { value: "INVESTMENT", label: "Investment" },
];

// ✅ FIXED: Match backend OCCASION_TYPES
const OCCASION_TYPES = [
  { value: "ENGAGEMENT", label: "Engagement" },
  { value: "WEDDING", label: "Wedding" },
  { value: "ANNIVERSARY", label: "Anniversary" },
  { value: "BIRTHDAY", label: "Birthday" },
  { value: "FESTIVAL", label: "Festival" },
  { value: "GIFT", label: "Gift" },
  { value: "SELF", label: "Self" },
  { value: "INVESTMENT", label: "Investment" },
  { value: "OTHER", label: "Other" },
];

const DURATIONS = [15, 30, 45, 60, 90, 120];

const AppointmentFieldUpdateModal = ({
  isOpen,
  onClose,
  appointment,
  fieldType, // 'type', 'duration', 'intent', 'priority', 'tags', 'metadata', 'reminders'
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);

  // Field-specific states
  const [selectedType, setSelectedType] = useState("");
  const [selectedDuration, setSelectedDuration] = useState(30);
  const [selectedPriority, setSelectedPriority] = useState("MEDIUM"); // ✅ FIXED: Default to MEDIUM
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState("");
  const [reason, setReason] = useState("");

  // ✅ FIXED: Intent fields structure matching backend model
  const [intent, setIntent] = useState({
    purposeType: "", // ✅ FIXED: Changed from 'purpose' to 'purposeType'
    occasionType: "", // ✅ FIXED: Changed from 'occasion' to 'occasionType'
    occasionDate: "",
    forWhom: "",
    budget: { min: "", max: "", flexible: false },
    preferences: {
      metals: [],
      styles: [],
      categories: [],
    },
    customerNotes: "",
    specialRequests: "",
  });

  // Reminder preferences
  const [reminderPreferences, setReminderPreferences] = useState({
    enabled: true,
    channels: ["SMS", "EMAIL"], // ✅ FIXED: Match backend structure
    timings: [1440, 120, 30], // 24 hours, 2 hours, 30 minutes
  });

  // Metadata
  const [metadata, setMetadata] = useState({});
  const [metadataKey, setMetadataKey] = useState("");
  const [metadataValue, setMetadataValue] = useState("");

  useEffect(() => {
    if (isOpen && appointment) {
      // ✅ FIXED: Use appointmentType instead of type
      setSelectedType(appointment.appointmentType || "");
      setSelectedDuration(appointment.duration || 30);
      setSelectedPriority(appointment.priority || "MEDIUM"); // ✅ FIXED: Default to MEDIUM
      setTags(appointment.tags || []);

      // ✅ FIXED: Handle intent object structure properly
      if (appointment.intent) {
        setIntent({
          purposeType:
            appointment.intent.purposeType || appointment.intent.purpose || "",
          occasionType:
            appointment.intent.occasionType ||
            appointment.intent.occasion ||
            "",
          occasionDate: appointment.intent.occasionDate || "",
          forWhom: appointment.intent.forWhom || "",
          budget: appointment.intent.budget || {
            min: "",
            max: "",
            flexible: false,
          },
          preferences: appointment.intent.preferences || {
            metals: [],
            styles: [],
            categories: [],
          },
          customerNotes: appointment.intent.customerNotes || "",
          specialRequests: appointment.intent.specialRequests || "",
        });
      }

      setReminderPreferences(
        appointment.reminderPreferences || reminderPreferences
      );
      setMetadata(appointment.metadata || {});
    }
  }, [isOpen, appointment]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      switch (fieldType) {
        case "type":
          await dispatch(
            updateAppointmentTypeThunk({
              appointmentId: appointment._id,
              type: selectedType, // Backend expects 'type' in the request body
              reason: reason || "Type updated",
            })
          ).unwrap();
          toast.success("Appointment type updated");
          break;

        case "duration":
          await dispatch(
            updateAppointmentDurationThunk({
              appointmentId: appointment._id,
              duration: selectedDuration,
              reason: reason || "Duration updated",
            })
          ).unwrap();
          toast.success("Duration updated");
          break;

        case "intent":
          await dispatch(
            updateAppointmentIntentThunk({
              appointmentId: appointment._id,
              intent, // Send the entire intent object
            })
          ).unwrap();
          toast.success("Intent updated");
          break;

        case "priority":
          await dispatch(
            updateAppointmentPriorityThunk({
              appointmentId: appointment._id,
              priority: selectedPriority,
              reason: reason || "Priority updated",
            })
          ).unwrap();
          toast.success("Priority updated");
          break;

        case "tags":
          // This is handled by add/remove tag buttons
          toast.info("Use the Add/Remove buttons for tags");
          break;

        case "metadata":
          await dispatch(
            updateAppointmentMetadataThunk({
              appointmentId: appointment._id,
              metadata,
            })
          ).unwrap();
          toast.success("Metadata updated");
          break;

        case "reminders":
          await dispatch(
            updateReminderPreferencesThunk({
              appointmentId: appointment._id,
              reminderPreferences,
            })
          ).unwrap();
          toast.success("Reminder preferences updated");
          break;

        default:
          toast.error("Unknown field type");
          return;
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Update error:", error);
      toast.error(error || "Failed to update appointment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddTag = async () => {
    if (!newTag.trim()) {
      toast.error("Please enter a tag");
      return;
    }

    setSubmitting(true);
    try {
      await dispatch(
        addAppointmentTagsThunk({
          appointmentId: appointment._id,
          tags: [newTag.trim()],
        })
      ).unwrap();

      setTags([...tags, newTag.trim()]);
      setNewTag("");
      toast.success("Tag added");
      onSuccess?.();
    } catch (error) {
      console.error("Add tag error:", error);
      toast.error(error || "Failed to add tag");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveTag = async (tagToRemove) => {
    setSubmitting(true);
    try {
      await dispatch(
        removeAppointmentTagsThunk({
          appointmentId: appointment._id,
          tags: [tagToRemove],
        })
      ).unwrap();

      setTags(tags.filter((t) => t !== tagToRemove));
      toast.success("Tag removed");
      onSuccess?.();
    } catch (error) {
      console.error("Remove tag error:", error);
      toast.error(error || "Failed to remove tag");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddMetadata = () => {
    if (!metadataKey.trim() || !metadataValue.trim()) {
      toast.error("Please enter both key and value");
      return;
    }

    setMetadata({
      ...metadata,
      [metadataKey.trim()]: metadataValue.trim(),
    });
    setMetadataKey("");
    setMetadataValue("");
  };

  const handleRemoveMetadata = (key) => {
    const newMetadata = { ...metadata };
    delete newMetadata[key];
    setMetadata(newMetadata);
  };

  const renderContent = () => {
    switch (fieldType) {
      case "type":
        return (
          <div>
            <label className="block text-sm font-medium text-text mb-3">
              Appointment Type
            </label>
            <div className="grid grid-cols-2 gap-3 max-h-96 overflow-y-auto">
              {APPOINTMENT_TYPES.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setSelectedType(type.value)}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    selectedType === type.value
                      ? "border-primary bg-primary-light"
                      : "border-neutral-light hover:border-primary-light"
                  }`}
                >
                  <div className="font-semibold text-text">{type.label}</div>
                </button>
              ))}
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-text mb-2">
                Reason (Optional)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why is the type being changed?"
                className="w-full border border-neutral-light rounded-lg px-3 py-2 text-text focus:outline-none focus:ring-2 focus:ring-primary"
                rows={2}
              />
            </div>
          </div>
        );

      case "duration":
        return (
          <div>
            <label className="block text-sm font-medium text-text mb-3">
              Duration (minutes)
            </label>
            <div className="grid grid-cols-3 gap-3">
              {DURATIONS.map((duration) => (
                <button
                  key={duration}
                  onClick={() => setSelectedDuration(duration)}
                  className={`p-3 rounded-lg border-2 text-center transition-all ${
                    selectedDuration === duration
                      ? "border-primary bg-primary-light"
                      : "border-neutral-light hover:border-primary-light"
                  }`}
                >
                  <FiClock className="mx-auto mb-1 text-primary" />
                  <div className="font-semibold text-text">{duration} min</div>
                </button>
              ))}
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-text mb-2">
                Reason (Optional)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why is the duration being changed?"
                className="w-full border border-neutral-light rounded-lg px-3 py-2 text-text focus:outline-none focus:ring-2 focus:ring-primary"
                rows={2}
              />
            </div>
          </div>
        );

      case "priority":
        return (
          <div>
            <label className="block text-sm font-medium text-text mb-3">
              Priority Level
            </label>
            <div className="space-y-2">
              {PRIORITIES.map((priority) => (
                <button
                  key={priority.value}
                  onClick={() => setSelectedPriority(priority.value)}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all flex items-center gap-3 ${
                    selectedPriority === priority.value
                      ? "border-primary"
                      : "border-neutral-light hover:border-primary-light"
                  }`}
                >
                  <FiFlag className="text-primary" />
                  <div className="flex-1">
                    <div className="font-semibold text-text">
                      {priority.label}
                    </div>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${priority.color}`}
                  >
                    {priority.value}
                  </span>
                </button>
              ))}
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-text mb-2">
                Reason (Optional)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why is the priority being changed?"
                className="w-full border border-neutral-light rounded-lg px-3 py-2 text-text focus:outline-none focus:ring-2 focus:ring-primary"
                rows={2}
              />
            </div>
          </div>
        );

      case "tags":
        return (
          <div>
            <label className="block text-sm font-medium text-text mb-3">
              Tags
            </label>

            {/* Current Tags */}
            <div className="flex flex-wrap gap-2 mb-4">
              {tags.length === 0 ? (
                <p className="text-sm text-neutral-dark italic">
                  No tags added yet
                </p>
              ) : (
                tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-2 bg-primary-light text-primary px-3 py-1 rounded-full text-sm"
                  >
                    <FiTag size={14} />
                    {tag}
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      disabled={submitting}
                      className="hover:text-red-600 transition-colors"
                    >
                      <FiX size={16} />
                    </button>
                  </span>
                ))
              )}
            </div>

            {/* Add New Tag */}
            <div className="flex gap-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
                placeholder="Enter new tag..."
                className="flex-1 border border-neutral-light rounded-lg px-3 py-2 text-text focus:outline-none focus:ring-2 focus:ring-primary"
                disabled={submitting}
              />
              <Button
                variant="primary"
                onClick={handleAddTag}
                disabled={!newTag.trim() || submitting}
              >
                {submitting ? "Adding..." : "Add"}
              </Button>
            </div>

            <p className="mt-3 text-xs text-neutral-dark">
              Tags are saved immediately when added or removed
            </p>
          </div>
        );

      case "intent":
        return (
          <div className="space-y-4 max-h-96 overflow-y-auto p-3">
            <label className="block text-sm font-medium text-text mb-3">
              Customer Intent & Preferences
            </label>

            {/* Purpose Type */}
            <div>
              <label className="block text-sm font-medium text-neutral-dark mb-2">
                Purpose of Visit
              </label>
              <select
                value={intent.purposeType}
                onChange={(e) =>
                  setIntent({ ...intent, purposeType: e.target.value })
                }
                className="w-full border border-neutral-light rounded-lg px-3 py-2 text-text focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Select purpose...</option>
                {PURPOSE_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Occasion Type */}
            <div>
              <label className="block text-sm font-medium text-neutral-dark mb-2">
                Occasion
              </label>
              <select
                value={intent.occasionType}
                onChange={(e) =>
                  setIntent({ ...intent, occasionType: e.target.value })
                }
                className="w-full border border-neutral-light rounded-lg px-3 py-2 text-text focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Select occasion...</option>
                {OCCASION_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Budget */}
            <div>
              <label className="block text-sm font-medium text-neutral-dark mb-2">
                Budget Range (₹)
              </label>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="number"
                  value={intent.budget?.min || ""}
                  onChange={(e) =>
                    setIntent({
                      ...intent,
                      budget: { ...intent.budget, min: e.target.value },
                    })
                  }
                  placeholder="Min"
                  className="border border-neutral-light rounded-lg px-3 py-2 text-text focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <input
                  type="number"
                  value={intent.budget?.max || ""}
                  onChange={(e) =>
                    setIntent({
                      ...intent,
                      budget: { ...intent.budget, max: e.target.value },
                    })
                  }
                  placeholder="Max"
                  className="border border-neutral-light rounded-lg px-3 py-2 text-text focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <label className="flex items-center gap-2 mt-2 text-sm">
                <input
                  type="checkbox"
                  checked={intent.budget?.flexible || false}
                  onChange={(e) =>
                    setIntent({
                      ...intent,
                      budget: { ...intent.budget, flexible: e.target.checked },
                    })
                  }
                  className="rounded"
                />
                <span className="text-neutral-dark">Budget is flexible</span>
              </label>
            </div>

            {/* Customer Notes */}
            <div>
              <label className="block text-sm font-medium text-neutral-dark mb-2">
                Customer Notes
              </label>
              <textarea
                value={intent.customerNotes || ""}
                onChange={(e) =>
                  setIntent({ ...intent, customerNotes: e.target.value })
                }
                placeholder="Additional notes from customer..."
                className="w-full border border-neutral-light rounded-lg px-3 py-2 text-text focus:outline-none focus:ring-2 focus:ring-primary"
                rows={3}
              />
            </div>

            {/* Special Requests */}
            <div>
              <label className="block text-sm font-medium text-neutral-dark mb-2">
                Special Requests
              </label>
              <textarea
                value={intent.specialRequests || ""}
                onChange={(e) =>
                  setIntent({ ...intent, specialRequests: e.target.value })
                }
                placeholder="Any special requests or requirements..."
                className="w-full border border-neutral-light rounded-lg px-3 py-2 text-text focus:outline-none focus:ring-2 focus:ring-primary"
                rows={2}
              />
            </div>
          </div>
        );

      case "reminders":
        return (
          <div className="space-y-4">
            <label className="block text-sm font-medium text-text mb-3">
              Reminder Preferences
            </label>

            {/* Enable/Disable Reminders */}
            <div className="p-4 border border-neutral-light rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FiBell className="text-primary" />
                  <span className="font-medium">Enable Reminders</span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={reminderPreferences.enabled}
                    onChange={(e) =>
                      setReminderPreferences({
                        ...reminderPreferences,
                        enabled: e.target.checked,
                      })
                    }
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
            </div>

            {/* Reminder Channels */}
            {reminderPreferences.enabled && (
              <>
                <div>
                  <label className="block text-sm font-medium text-neutral-dark mb-2">
                    Notification Channels
                  </label>
                  <div className="space-y-2">
                    {["SMS", "EMAIL", "PUSH", "WHATSAPP"].map((channel) => (
                      <label key={channel} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={reminderPreferences.channels?.includes(
                            channel
                          )}
                          onChange={(e) => {
                            const channels = reminderPreferences.channels || [];
                            if (e.target.checked) {
                              setReminderPreferences({
                                ...reminderPreferences,
                                channels: [...channels, channel],
                              });
                            } else {
                              setReminderPreferences({
                                ...reminderPreferences,
                                channels: channels.filter((c) => c !== channel),
                              });
                            }
                          }}
                          className="rounded"
                        />
                        <span className="text-sm">{channel}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-dark mb-2">
                    Reminder Timings (minutes before appointment)
                  </label>
                  <div className="text-xs text-neutral-dark mb-2">
                    Current: {reminderPreferences.timings?.join(", ")} minutes
                  </div>
                  <p className="text-xs text-neutral-dark italic">
                    Default: 24 hours (1440 min), 2 hours (120 min), 30 minutes
                  </p>
                </div>
              </>
            )}
          </div>
        );

      case "metadata":
        return (
          <div>
            <label className="block text-sm font-medium text-text mb-3">
              Custom Metadata
            </label>

            {/* Current Metadata */}
            <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
              {Object.keys(metadata).length === 0 ? (
                <p className="text-sm text-neutral-dark italic">
                  No custom metadata added yet
                </p>
              ) : (
                Object.entries(metadata).map(([key, value]) => (
                  <div
                    key={key}
                    className="flex items-center justify-between p-3 bg-neutral-light rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="font-medium text-sm text-text">{key}</div>
                      <div className="text-sm text-neutral-dark">
                        {String(value)}
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveMetadata(key)}
                      className="text-red-600 hover:text-red-700 ml-2"
                    >
                      <FiX size={18} />
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Add New Metadata */}
            <div className="space-y-2">
              <input
                type="text"
                value={metadataKey}
                onChange={(e) => setMetadataKey(e.target.value)}
                placeholder="Key (e.g., source, campaign)"
                className="w-full border border-neutral-light rounded-lg px-3 py-2 text-text focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <input
                type="text"
                value={metadataValue}
                onChange={(e) => setMetadataValue(e.target.value)}
                placeholder="Value"
                className="w-full border border-neutral-light rounded-lg px-3 py-2 text-text focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <Button
                variant="outline"
                onClick={handleAddMetadata}
                disabled={!metadataKey.trim() || !metadataValue.trim()}
                className="w-full"
              >
                Add Metadata Field
              </Button>
            </div>

            <p className="mt-3 text-xs text-neutral-dark">
              Click "Update" button below to save all metadata changes
            </p>
          </div>
        );

      default:
        return (
          <div className="text-center py-8">
            <FiAlertCircle
              className="mx-auto mb-2 text-neutral-dark"
              size={48}
            />
            <p className="text-neutral-dark">Unknown field type</p>
          </div>
        );
    }
  };

  const getModalTitle = () => {
    const titles = {
      type: "Update Appointment Type",
      duration: "Update Duration",
      intent: "Update Customer Intent",
      priority: "Update Priority",
      tags: "Manage Tags",
      metadata: "Manage Metadata",
      reminders: "Reminder Preferences",
    };
    return titles[fieldType] || "Update Appointment";
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-light flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-text">{getModalTitle()}</h2>
            <p className="text-sm text-neutral-dark mt-1">
              {appointment?.appointmentCode}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-neutral-dark hover:text-text transition-colors"
            disabled={submitting}
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {renderContent()}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-neutral-light flex items-center justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          {fieldType !== "tags" && (
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? "Updating..." : "Update"}
            </Button>
          )}
          {fieldType === "tags" && (
            <p className="text-xs text-neutral-dark">
              Tags are saved when added/removed
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppointmentFieldUpdateModal;
